import { collectionMetafields } from '~/lib/shopify/constants/metafields'
import { getStorePublicCredentials } from '~/lib/shopify/credentials/public'
import { metafieldsToObject } from '~/lib/shopify/utils/metafield-to-object'
import getStoryblokProductsFromShopifyIds from '~/lib/storyblok/get-product-by-shopify-id'

import {
  GID,
  sfCollection,
  sfProductsByIds,
} from '@unlikelystudio/commerce-connector'
import type { Product } from '@unlikelystudio/react-ecommerce-hooks'

import { CardProductProps } from '~/components/UI/CardProduct'

import { getShopifyProductIdFromStoryblokData } from '~/utils/get-shopify-id-from-storyblok-data'
import { getLang } from '~/utils/locales'
import { objectValues } from '~/utils/object-values'

import serializeProductCard from '~/data/product-card-data'

export type Collection = {
  products: {
    edges: {
      node: Product
    }[]
  }
}

export type getProductUpsellsProps = {
  products: string[]
  locale: string
}

/**
 * It takes an array of product ids, finds the collections they belong to, finds the metafields of
 * those collections, finds the collections that are linked to those metafields, finds the products
 * that are linked to those collections, filters out the products that are already in the array, and
 * then returns the remaining products
 * @param products - An array of Shopify product IDs
 * @param locale - The locale of the current page.
 * @returns An array of products
 */
export async function getProductsUpsales(
  ids: GID[],
  locale: string,
): Promise<CardProductProps[]> {
  const shop = getStorePublicCredentials()
  const languageCode = getLang(locale)?.toUpperCase()

  if (!ids || ids.length === 0) return null

  const response = await sfProductsByIds(
    shop,
    {
      ids,
      collectionsFirst: 25,
      metafieldCollectionsKeys: objectValues(collectionMetafields),
    },
    languageCode,
  )

  if (!Boolean(response.products.length)) return null
  const products = response.products.filter(Boolean)
  /* Taking the collections from the products and reducing them to a single array of upsales collections. */
  const processedUpsalesCollections = products
    ?.filter(Boolean)
    .reduce((acc, product) => {
      product?.collections?.forEach((collection) => {
        const metafields = metafieldsToObject(
          collection?.metafields,
          collectionMetafields,
        )
        const upsales = metafields.upsales
        if (upsales && acc?.indexOf(upsales) === -1) {
          acc?.push(upsales)
        }
      })
      return acc
    }, [])

  if (!processedUpsalesCollections || processedUpsalesCollections.length === 0)
    return null

  const upsales = await Promise.all(
    processedUpsalesCollections
      .map(
        async (collectionId) =>
          (
            await sfCollection(
              shop,
              { id: collectionId, productsFirst: 10 },
              languageCode,
            )
          ).collection,
      )
      .filter(Boolean),
  )

  /* It's taking the products from the upsales collections and reducing them to a single array of
  products. */
  const upsalesByProducts = upsales?.map((collection) =>
    collection?.products?.map((product) => product.id),
  )

  /**
   * Here we are reordering our upsales in order to follow the next pattern.
   * - if multiple collections of upsales
   *   - display the first product of each collection
   *   - display the second ... and so on
   *
   */
  const formattedUpsales = upsalesByProducts
    .reduce((acc, next) => {
      next?.forEach((_, i) => {
        if (!acc[i]) acc[i] = []
        acc[i] = [...acc[i], next[i]]
      })
      return acc
    }, [] as GID[][])
    .flat()

  /* It's filtering out the products that are already in the array or are part of the products array provided by the cart. */
  const filteredUpsales =
    formattedUpsales.filter(
      (element, index) =>
        formattedUpsales.indexOf(element) === index &&
        ids?.indexOf(element) === -1,
    ) ?? []

  /* It's getting the Storyblok products from the Shopify IDs. */
  const storyblokProducts = await getStoryblokProductsFromShopifyIds(
    filteredUpsales,
    locale,
  )

  /* We need to reorder our fetched products to meet the ordering we setup before */
  const orderStoryblokProducts =
    storyblokProducts.reduce((acc, item) => {
      const pluginId = getShopifyProductIdFromStoryblokData(item)
      if (pluginId) acc[pluginId] = item

      return acc
    }, {}) ?? []

  const processedProducts = filteredUpsales
    ?.map((item) => {
      return orderStoryblokProducts?.[item] ?? null
    })
    .filter((item) => item)

  /* Serialize our products */
  const serializedProducts = (
    await Promise.all(
      processedProducts?.map(async (product) => {
        return (await serializeProductCard(product, locale)) ?? null
      }),
    )
  )?.filter((product) => product?.defaultVariant?.availableForSale)

  return serializedProducts
}
